import { useState } from 'react';
import type { EquipePlacar } from '../types';
import CartaoEquipe from './CartaoEquipe';
import DicaPrimeiraVez from '@/components/feature/DicaPrimeiraVez';

export type Selecao = number | 'sem-equipe' | null;

interface Props {
  equipes: EquipePlacar[];
  carregando: boolean;
  nSemEquipe: number;
  selecionado: Selecao;
  aoSelecionar: (sel: Selecao) => void;
  aoSalvarVagas: (equipe: EquipePlacar, vagas: number) => Promise<boolean>;
  aoCriarEquipe: (nome: string) => Promise<boolean>;
  aoCriarPadrao: () => Promise<void>;
}

export default function ColunaEquipes({
  equipes,
  carregando,
  nSemEquipe,
  selecionado,
  aoSelecionar,
  aoSalvarVagas,
  aoCriarEquipe,
  aoCriarPadrao,
}: Props) {
  const [criarAberto, setCriarAberto] = useState(false);
  const [nomeNova, setNomeNova] = useState('');
  const [salvandoCriar, setSalvandoCriar] = useState(false);
  const [criandoPadrao, setCriandoPadrao] = useState(false);

  async function confirmarCriar() {
    const nome = nomeNova.trim();
    if (!nome) return;
    setSalvandoCriar(true);
    const ok = await aoCriarEquipe(nome);
    setSalvandoCriar(false);
    if (ok) {
      setNomeNova('');
      setCriarAberto(false);
    }
  }

  async function criarPadrao() {
    setCriandoPadrao(true);
    await aoCriarPadrao();
    setCriandoPadrao(false);
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => aoSelecionar('sem-equipe')}
        className={`w-full cursor-pointer rounded-3xl bg-white p-4 text-left shadow-[0_6px_18px_rgba(20,25,60,.06)] transition-colors ${
          selecionado === 'sem-equipe'
            ? 'ring-2 ring-primary-500'
            : 'hover:bg-primary-50'
        }`}
      >
        <p className="text-sm font-semibold text-foreground-950">Sem equipe</p>
        <p className="mt-1 text-sm text-secondary-500">{nSemEquipe} pessoas</p>
      </button>

      {carregando ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-secondary-100" />
          ))}
        </div>
      ) : (
        <>
          {equipes.map((e) => (
            <CartaoEquipe
              key={e.equipe_id}
              equipe={e}
              selecionado={selecionado === e.equipe_id}
              onSelect={() => aoSelecionar(e.equipe_id)}
              onSalvarVagas={(vagas) => aoSalvarVagas(e, vagas)}
            />
          ))}

          {equipes.length === 0 && <DicaPrimeiraVez id="painel.equipes.vazio" />}

          {equipes.length === 0 && (
            <button
              type="button"
              onClick={criarPadrao}
              disabled={criandoPadrao}
              className="w-full cursor-pointer whitespace-nowrap rounded-2xl border border-dashed border-primary-400 bg-primary-50 px-4 py-4 text-center text-[14px] font-semibold text-primary-600 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {criandoPadrao ? 'Criando…' : 'Criar as 18 equipes do acampamento'}
            </button>
          )}

          {criarAberto ? (
            <div className="rounded-lg border border-secondary-200 bg-white p-3">
              <input
                autoFocus
                type="text"
                value={nomeNova}
                placeholder="Nome da equipe"
                onChange={(e) => setNomeNova(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmarCriar();
                  if (e.key === 'Escape') setCriarAberto(false);
                }}
                className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-400"
              />
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={confirmarCriar}
                  disabled={!nomeNova.trim() || salvandoCriar}
                  className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Criar
                </button>
                <button
                  type="button"
                  onClick={() => setCriarAberto(false)}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCriarAberto(true)}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-primary-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-add-line" />
              </span>
              Nova equipe
            </button>
          )}
        </>
      )}
    </div>
  );
}